/** Central place every gateway/shell component reads build-time config from — no module reaches into
 * `import.meta.env` directly, so there's exactly one place to check when adding a new variable. */
export const useMocks = (import.meta.env.VITE_USE_MOCKS ?? 'true').toLowerCase() !== 'false';

/**
 * Business Profile Setup ("Company Details") is the one feature whose Playwright suite predates its
 * Mock/Http gateway split and depends on `page.route()` intercepting real fetch calls per-test (see
 * tests/e2e/business-profile-setup.spec.ts). Playwright's own dev server (playwright.config.ts) sets
 * VITE_USE_MOCKS=true globally because every OTHER feature's Playwright suite (e.g. dashboard-visual)
 * relies on that flag selecting its own Mock*Gateway fixture. Without this override, turning on the new
 * MockBusinessProfileSetupGateway would make it inherit VITE_USE_MOCKS=true too, and its in-memory
 * fixture would never call fetch — so page.route() would never fire and every test in that suite would
 * hang waiting for form fields that the real submitted data never populates.
 *
 * Defaults to whatever VITE_USE_MOCKS resolved to (so normal dev/build behavior is unchanged and this
 * gateway still only mocks when VITE_USE_MOCKS=true), and is only ever overridden by
 * playwright.config.ts's webServer command, which sets it to 'false' to keep that one suite's existing
 * HTTP-level mocking intact.
 */
export const useBusinessProfileSetupMocks =
  import.meta.env.VITE_USE_BUSINESS_PROFILE_MOCKS === undefined
    ? useMocks
    : import.meta.env.VITE_USE_BUSINESS_PROFILE_MOCKS.toLowerCase() !== 'false';

export const stagingBannerEnabled = (import.meta.env.VITE_STAGING_BANNER ?? 'true').toLowerCase() !== 'false';

/**
 * Every module is called through a RELATIVE path prefix, never an absolute cross-origin URL — see
 * HttpIdentityGateway's own comment on why (SameSite=Strict cookies + no separate CSRF-token defense)
 * and vite.config.ts's proxy block for the mechanics. In dev, Vite's own proxy strips the prefix and
 * forwards to that module's real port (still configured via VITE_*_API_BASE_URL, read there). In a real
 * deployment, ops' reverse proxy performs the identical path-prefix routing under one origin — see
 * docs/staging-deployment.md. Nothing in this file should ever be an absolute `https://...` URL; that
 * was tried during this build and confirmed broken under browser "schemeful same-site" cookie rules
 * (the frontend serves over plain HTTP in dev, every API serves HTTPS — a direct cross-origin fetch is
 * treated as cross-site and the access-token cookie is correctly withheld).
 */
export const employeesApiBaseUrl = '/svc-employees';
export const payrollConfigApiBaseUrl = '/svc-payrollconfig';
export const compensationApiBaseUrl = '/svc-compensation';
export const timeAndLeaveApiBaseUrl = '/svc-timeandleave';
export const customPayRunApiBaseUrl = '/svc-custompayrun';
export const accountingApiBaseUrl = '/svc-accounting';
export const payrollRunApiBaseUrl = '/svc-payrollreview';
export const reportsApiBaseUrl = '/svc-reports';
export const subscriptionsApiBaseUrl = '/svc-subscriptions';
export const dashboardApiBaseUrl = '/svc-dashboard';

// The remaining four exist solely so the Audit History screen can read each module's own
// /api/audit-events — none of these modules has a dedicated frontend feature yet. See
// docs/audit-history-architecture.md.
export const payrollCalculationApiBaseUrl = '/svc-payrollcalculation';
export const paymentProcessingApiBaseUrl = '/svc-paymentprocessing';
export const taxComplianceApiBaseUrl = '/svc-taxcompliance';
export const taxFormsApiBaseUrl = '/svc-taxforms';
